import * as React from 'react'

interface IProps {
  children: React.ReactNode
  /** Minimum height (px) of the child item. Used as placeholder until the item enters the viewport. */
  minHeight: number
  /** Optional scroll container to observe against instead of the viewport.
   *  Pass the element directly — use a useState callback ref on the caller side so React
   *  re-renders (and re-runs this effect) once the container is attached to the DOM. */
  root?: Element | null
}

/**
 * Drop-in replacement for react-lazyload (removed because it relies on
 * ReactDOM.findDOMNode which was dropped in React 19).
 *
 * Uses the native IntersectionObserver API instead of scroll listeners:
 * - rootMargin '200px': starts loading content 200px before it enters the viewport.
 * - height placeholder: reserves space so off-screen items don't all appear "in view" at once.
 * - observer.disconnect(): once visible the observer is torn down — no ongoing listeners.
 * - Once rendered, children stay mounted even if the item scrolls out of view (intentional for
 *   feed lists — avoids remounting). This component is not suitable for cases that require
 *   element recycling or teardown on scroll-out.
 */
const LazyLoad = ({children, minHeight, root}: IProps) => {
  const ref = React.useRef<HTMLDivElement>(null)
  const [inView, setInView] = React.useState(false)

  React.useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true)
          observer.disconnect()
        }
      },
      {rootMargin: '200px', root: root ?? null}
    )
    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [root])

  return (
    <div ref={ref} style={{minHeight: inView ? undefined : minHeight}}>
      {inView && children}
    </div>
  )
}

export default LazyLoad
